'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ApplyModal from './ApplyModal';

type Step = 1 | 2 | 3 | 'done';

const roleTypes = [
  'Engineering (IC)',
  'Engineering (Management)',
  'Product Management',
  'Design',
  'Operations',
  'Data & Analytics',
  'Customer Success',
  'Other',
];

const salaryRanges = [
  '$80k – $120k',
  '$120k – $160k',
  '$160k – $200k',
  '$200k – $250k',
  '$250k+',
];

interface MatchedJob {
  id: number;
  title: string;
  company: string;
  companyAbbr: string;
  companyColor: string;
  salaryMin: number;
  salaryMax: number;
  timezone: string;
  tags: string[];
  category: string;
  minSalaryK: number;
}

const allMatchedJobs: MatchedJob[] = [
  {
    id: 101,
    title: 'Staff Software Engineer',
    company: 'GitLab',
    companyAbbr: 'GL',
    companyColor: '#FC6D26',
    salaryMin: 180,
    salaryMax: 230,
    timezone: 'UTC-8 – UTC+2',
    tags: ['Ruby', 'Go', 'Kubernetes'],
    category: 'Engineering (IC)',
    minSalaryK: 180,
  },
  {
    id: 102,
    title: 'Senior Frontend Engineer',
    company: 'Vercel',
    companyAbbr: 'VC',
    companyColor: '#000000',
    salaryMin: 160,
    salaryMax: 210,
    timezone: 'Americas / Europe',
    tags: ['React', 'TypeScript', 'Next.js'],
    category: 'Engineering (IC)',
    minSalaryK: 160,
  },
  {
    id: 103,
    title: 'Backend Engineer, Infra',
    company: 'Doist',
    companyAbbr: 'DO',
    companyColor: '#DB4035',
    salaryMin: 130,
    salaryMax: 170,
    timezone: 'UTC-3 – UTC+8',
    tags: ['Python', 'AWS', 'Terraform'],
    category: 'Engineering (IC)',
    minSalaryK: 130,
  },
  {
    id: 104,
    title: 'Engineering Manager, Platform',
    company: 'Vercel',
    companyAbbr: 'VC',
    companyColor: '#000000',
    salaryMin: 200,
    salaryMax: 260,
    timezone: 'Americas / Europe',
    tags: ['Distributed Systems', 'Node.js', 'Leadership'],
    category: 'Engineering (Management)',
    minSalaryK: 200,
  },
  {
    id: 105,
    title: 'Director of Engineering',
    company: 'Buffer',
    companyAbbr: 'BF',
    companyColor: '#168EEA',
    salaryMin: 190,
    salaryMax: 240,
    timezone: 'Global',
    tags: ['Team Building', 'Roadmap', 'Remote'],
    category: 'Engineering (Management)',
    minSalaryK: 190,
  },
  {
    id: 106,
    title: 'Senior Product Manager',
    company: 'Zapier',
    companyAbbr: 'ZP',
    companyColor: '#FF4A00',
    salaryMin: 150,
    salaryMax: 195,
    timezone: 'Americas',
    tags: ['PLG', 'B2B SaaS', 'Growth'],
    category: 'Product Management',
    minSalaryK: 150,
  },
  {
    id: 107,
    title: 'Senior Product Manager, API',
    company: 'Airtable',
    companyAbbr: 'AB',
    companyColor: '#1283DA',
    salaryMin: 155,
    salaryMax: 200,
    timezone: 'Americas',
    tags: ['API', 'Developer Platform', 'B2B'],
    category: 'Product Management',
    minSalaryK: 155,
  },
  {
    id: 108,
    title: 'Principal Product Designer',
    company: 'Linear',
    companyAbbr: 'LN',
    companyColor: '#5E6AD2',
    salaryMin: 160,
    salaryMax: 200,
    timezone: 'UTC-5 – UTC+5',
    tags: ['Figma', 'Design Systems', 'B2B'],
    category: 'Design',
    minSalaryK: 160,
  },
  {
    id: 109,
    title: 'Senior UX Designer',
    company: 'Notion',
    companyAbbr: 'NT',
    companyColor: '#000000',
    salaryMin: 130,
    salaryMax: 170,
    timezone: 'Global',
    tags: ['User Research', 'Prototyping', 'Figma'],
    category: 'Design',
    minSalaryK: 130,
  },
  {
    id: 110,
    title: 'Head of Revenue Operations',
    company: 'Notion',
    companyAbbr: 'NT',
    companyColor: '#000000',
    salaryMin: 170,
    salaryMax: 215,
    timezone: 'Global',
    tags: ['RevOps', 'Salesforce', 'SaaS'],
    category: 'Operations',
    minSalaryK: 170,
  },
  {
    id: 111,
    title: 'Senior Data Engineer',
    company: 'Doist',
    companyAbbr: 'DO',
    companyColor: '#DB4035',
    salaryMin: 130,
    salaryMax: 170,
    timezone: 'UTC-3 – UTC+8',
    tags: ['dbt', 'Snowflake', 'Python'],
    category: 'Data & Analytics',
    minSalaryK: 130,
  },
  {
    id: 112,
    title: 'Analytics Engineer',
    company: 'GitLab',
    companyAbbr: 'GL',
    companyColor: '#FC6D26',
    salaryMin: 140,
    salaryMax: 180,
    timezone: 'UTC-8 – UTC+2',
    tags: ['SQL', 'dbt', 'Looker'],
    category: 'Data & Analytics',
    minSalaryK: 140,
  },
  {
    id: 113,
    title: 'Senior Customer Success Manager',
    company: 'Hotjar',
    companyAbbr: 'HJ',
    companyColor: '#FD3A5C',
    salaryMin: 95,
    salaryMax: 125,
    timezone: 'Europe / Americas',
    tags: ['Enterprise', 'SaaS', 'Renewal'],
    category: 'Customer Success',
    minSalaryK: 95,
  },
  {
    id: 114,
    title: 'Director of People Operations',
    company: 'Buffer',
    companyAbbr: 'BF',
    companyColor: '#168EEA',
    salaryMin: 140,
    salaryMax: 175,
    timezone: 'Global',
    tags: ['Remote Culture', 'HRIS', 'L&D'],
    category: 'Other',
    minSalaryK: 140,
  },
];

function salaryMinFromRange(range: string): number {
  if (range === '$80k – $120k') return 80;
  if (range === '$120k – $160k') return 120;
  if (range === '$160k – $200k') return 160;
  if (range === '$200k – $250k') return 200;
  if (range === '$250k+') return 250;
  return 0;
}

function getMatchedJobs(roleType: string, salary: string): MatchedJob[] {
  const minSalary = salaryMinFromRange(salary);
  let filtered = allMatchedJobs.filter(
    (j) => j.category === roleType && j.salaryMax >= minSalary
  );
  if (filtered.length === 0) {
    filtered = allMatchedJobs.filter((j) => j.salaryMax >= minSalary).slice(0, 4);
  }
  return filtered;
}

function MatchedJobCard({ job }: { job: MatchedJob }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="bg-white rounded-xl border border-fog-gray shadow-card p-5 flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${job.companyColor}15`, border: `1px solid ${job.companyColor}25` }}
          >
            <span className="text-xs font-bold" style={{ color: job.companyColor }}>{job.companyAbbr}</span>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-light">{job.company}</p>
            <h4 className="text-sm font-semibold text-slate-dark leading-snug">{job.title}</h4>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-slate-light">
          <span className="flex items-center gap-1">
            <Icon name="BanknotesIcon" size={12} className="text-slate-light" />
            <span className="font-semibold text-slate-dark">${job.salaryMin}k – ${job.salaryMax}k</span>
          </span>
          <span className="flex items-center gap-1">
            <Icon name="ClockIcon" size={12} className="text-slate-light" />
            {job.timezone}
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {job.tags.map((tag) => (
            <span key={tag} className="text-[11px] font-medium text-slate-pencil bg-fog-gray/60 px-2 py-0.5 rounded-md">
              {tag}
            </span>
          ))}
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="mt-1 w-full text-sm font-semibold text-white bg-signal-blue hover:bg-blue-600 transition-colors py-2.5 rounded-lg"
        >
          Apply Now →
        </button>
      </div>

      {showModal && (
        <ApplyModal
          jobTitle={job.title}
          company={job.company}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

export default function LeadCapture() {
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState('');
  const [roleType, setRoleType] = useState('');
  const [salary, setSalary] = useState('');
  const [error, setError] = useState('');

  const handleStep1 = () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleStep2 = () => {
    if (!roleType) {
      setError('Please select a role type.');
      return;
    }
    setError('');
    setStep(3);
  };

  const handleStep3 = () => {
    if (!salary) {
      setError('Please select a salary range.');
      return;
    }
    setError('');
    setStep('done');
  };

  const matchedJobs = step === 'done' ? getMatchedJobs(roleType, salary) : [];

  return (
    <section id="get-matched" className="bg-cloud-white py-20">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-signal-blue uppercase tracking-widest mb-3">Weekly Digest</p>
          <h2 className="text-2xl font-semibold text-slate-dark tracking-tight mb-3">
            Get matched. Not overwhelmed.
          </h2>
          <p className="text-sm text-slate-light max-w-sm mx-auto leading-relaxed">
            Every Monday, a curated shortlist of senior remote roles that match your criteria. No noise. No algorithm. Just signal.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-fog-gray shadow-card overflow-hidden">
          {/* Progress bar */}
          {step !== 'done' && (
            <div className="h-0.5 bg-fog-gray">
              <div
                className="h-full bg-signal-blue transition-all duration-500"
                style={{ width: step === 1 ? '33%' : step === 2 ? '66%' : '100%' }}
              />
            </div>
          )}

          <div className="p-8">
            {step === 'done' ? (
              <div>
                {/* Success header */}
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-full bg-signal-blue/10 flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckIcon" size={28} className="text-signal-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-dark mb-2">You're on the list.</h3>
                  <p className="text-sm text-slate-light max-w-xs mx-auto">
                    Your first curated digest arrives Monday morning. Here are your matched roles right now:
                  </p>
                </div>

                {/* Matched jobs */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-semibold text-slate-pencil uppercase tracking-widest">
                      {matchedJobs.length} Matched Role{matchedJobs.length !== 1 ? 's' : ''} for You
                    </p>
                    <span className="text-xs font-medium text-signal-blue bg-signal-blue/8 px-2 py-0.5 rounded-full">
                      {roleType} · {salary}
                    </span>
                  </div>
                  <div className="flex flex-col gap-4">
                    {matchedJobs.map((job) => (
                      <MatchedJobCard key={job.id} job={job} />
                    ))}
                  </div>
                </div>

                <a
                  href="#jobs"
                  className="flex items-center justify-center gap-2 w-full text-sm font-semibold text-signal-blue border border-signal-blue/20 hover:bg-signal-blue/5 transition-colors py-3 rounded-lg"
                >
                  Browse all open roles →
                </a>
              </div>
            ) : (
              <>
                {/* Step 1: Email */}
                {step === 1 && (
                  <div className="form-step-enter">
                    <label className="block text-xs font-semibold text-slate-pencil uppercase tracking-widest mb-2">
                      Step 1 of 3 — Your email
                    </label>
                    <p className="text-sm text-slate-light mb-5">We'll send your weekly digest here. No account required.</p>
                    <div className="flex gap-3">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleStep1()}
                        placeholder="you@company.com"
                        className="flex-1 text-sm font-medium text-slate-dark bg-cloud-white border border-fog-gray rounded-lg px-4 py-3 focus:border-signal-blue focus:ring-2 focus:ring-signal-blue/10 outline-none transition-all"
                      />
                      <button
                        onClick={handleStep1}
                        className="flex items-center gap-2 bg-signal-blue text-white text-sm font-semibold px-5 py-3 rounded-lg hover:bg-blue-600 transition-colors shrink-0"
                      >
                        Continue <Icon name="ArrowRightIcon" size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Role type */}
                {step === 2 && (
                  <div className="form-step-enter">
                    <label className="block text-xs font-semibold text-slate-pencil uppercase tracking-widest mb-2">
                      Step 2 of 3 — Your discipline
                    </label>
                    <p className="text-sm text-slate-light mb-5">We'll filter roles to your area of expertise.</p>
                    <div className="grid grid-cols-2 gap-2 mb-5">
                      {roleTypes.map((r) => (
                        <button
                          key={r}
                          onClick={() => setRoleType(r)}
                          className={`text-sm font-medium px-3 py-2.5 rounded-lg border text-left transition-all ${
                            roleType === r
                              ? 'border-signal-blue bg-signal-blue/5 text-signal-blue' :'border-fog-gray text-slate-pencil hover:border-signal-blue/30 hover:bg-cloud-white'
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={handleStep2}
                      className="w-full flex items-center justify-center gap-2 bg-signal-blue text-white text-sm font-semibold py-3 rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Continue <Icon name="ArrowRightIcon" size={14} />
                    </button>
                  </div>
                )}

                {/* Step 3: Salary */}
                {step === 3 && (
                  <div className="form-step-enter">
                    <label className="block text-xs font-semibold text-slate-pencil uppercase tracking-widest mb-2">
                      Step 3 of 3 — Minimum salary
                    </label>
                    <p className="text-sm text-slate-light mb-5">We'll only show roles at or above this band.</p>
                    <div className="flex flex-col gap-2 mb-5">
                      {salaryRanges.map((s) => (
                        <button
                          key={s}
                          onClick={() => setSalary(s)}
                          className={`text-sm font-medium px-4 py-3 rounded-lg border text-left transition-all ${
                            salary === s
                              ? 'border-signal-blue bg-signal-blue/5 text-signal-blue' :'border-fog-gray text-slate-pencil hover:border-signal-blue/30'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={handleStep3}
                      className="w-full flex items-center justify-center gap-2 bg-signal-blue text-white text-sm font-semibold py-3 rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Get Matched Weekly <Icon name="CheckIcon" size={14} />
                    </button>
                  </div>
                )}

                {error && (
                  <p className="text-xs text-red-500 mt-3 font-medium">{error}</p>
                )}

                <p className="text-xs text-slate-light mt-4 text-center">
                  No account. No credit card. Unsubscribe any time.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Employer path */}
        <div id="post-role" className="mt-8 text-center">
          <p className="text-sm text-slate-light">
            Hiring distributed talent?{' '}
            <a href="#" className="text-signal-blue font-semibold hover:text-blue-700 transition-colors">
              Post a role → Qualifier form
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}